import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import { CheckIcon } from '@c2fo/liquidity/icons';
import clipboardSvg from '@/enterprise/assets/clipboard.svg?url';
import { useReporting } from '@/reporting';
import levelsSvg from '@/shared/assets/levels.svg?url';
import lightbulbPlainSvg from '@/shared/assets/lightbulbPlain.svg?url';

interface RateCardProps {
  title: string;
  description: string;
  bestFor: string;
  icon: React.ReactNode;
  isSelected: boolean;
  onSelect: () => void;
}

const RateCard = ({ title, description, bestFor, icon, isSelected, onSelect }: RateCardProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col rounded-md border bg-white shadow-md sm:grid sm:[grid-row:span_3] sm:[grid-template-rows:subgrid]">
      <div className="p-4 pb-0">
        <div className="flex justify-between gap-1.5">
          <div>
            <p className="w-full text-lg font-bold">{title}</p>
            <p className="mt-0.5 text-sm text-text-secondary">{description}</p>
          </div>
          {icon}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-1 px-4 text-sm font-medium">
          <img src={lightbulbPlainSvg} className="mt-0.5 h-5 w-5 shrink-0" alt="" />
          <span>{bestFor}</span>
        </div>
      </div>
      <div className="p-4 pt-0">
        <Button
          className="w-full transition-[color,background-color,border-color,text-decoration-color,fill,stroke]"
          size="sm"
          variant={isSelected ? 'primary' : 'secondary'}
          onClick={onSelect}
          {...(isSelected && { icon: <CheckIcon className="fill-white [&>svg]:h-5 [&>svg]:w-5" /> })}
        >
          {isSelected ? t('core.selected') : t('core.select')}
        </Button>
      </div>
    </div>
  );
};

interface RecurringDiscountConfirmModalProps {
  open: boolean;
  isAlreadyDiscount: boolean;
  onConfirmDiscount: () => void;
  onSwitchToApr: () => void;
  onClose: () => void;
}

type RateSelection = 'APR' | 'DISC';

const RecurringDiscountConfirmModal = ({
  open,
  isAlreadyDiscount,
  onConfirmDiscount,
  onSwitchToApr,
  onClose,
}: RecurringDiscountConfirmModalProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const originalRateType: RateSelection = isAlreadyDiscount ? 'DISC' : 'APR';
  const [selected, setSelected] = useState<RateSelection>(originalRateType);

  useEffect(() => {
    if (open) {
      setSelected(originalRateType);
      trackEnterpriseEvent('recurring-discount-confirm-modal::shown');
    }
  }, [open, originalRateType, trackEnterpriseEvent]);

  const handleDismiss = () => {
    trackEnterpriseEvent('recurring-discount-confirm-modal::closed', {
      originalRateType,
      selectedRateType: selected,
      dismissed: true,
    });
    onClose();
  };

  const handleContinue = () => {
    trackEnterpriseEvent('recurring-discount-confirm-modal::closed', {
      originalRateType,
      selectedRateType: selected,
      dismissed: false,
    });

    if (selected === 'DISC') {
      onConfirmDiscount();
    } else {
      onSwitchToApr();
    }
  };

  return (
    <Modal onClose={handleDismiss} open={open} className="max-w-[600px]">
      <ModalTitleInverse>{t('builder.recurringDiscountNoticeTitle')}</ModalTitleInverse>
      <ModalContent className="space-y-3">
        <p>{t('builder.recurringDiscountSelectRate')}</p>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:grid-rows-[auto_auto_auto]">
          <RateCard
            title={t('core.apr')}
            description={t('builder.recurringDiscountAprDescription')}
            bestFor={t('builder.recurringDiscountAprBestFor')}
            icon={<img src={levelsSvg} className="h-[75px] w-[65px] shrink-0 grow-0" alt="" />}
            isSelected={selected === 'APR'}
            onSelect={() => setSelected('APR')}
          />
          <RateCard
            title={t('core.discount')}
            description={t('builder.recurringDiscountDiscDescription')}
            bestFor={t('builder.recurringDiscountDiscBestFor')}
            icon={<img src={clipboardSvg} className="h-[75px] w-[65px] shrink-0 grow-0" alt="" />}
            isSelected={selected === 'DISC'}
            onSelect={() => setSelected('DISC')}
          />
        </div>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={handleContinue}>
          {t('core.continue')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default RecurringDiscountConfirmModal;
